import { useEffect, useMemo, useRef, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	ButtonBase,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	ListItemText,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import Popover from "@mui/material/Popover";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { enUS } from "date-fns/locale";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { isMboLfsQtyViewer } from "../data";
import { downloadTable } from "../exportTable";
import { KpiStat, Panel, compactInputSx, outlinedButtonSx, primaryButtonSx } from "../sharedStyles";

const FY_START_MONTH = 3;
const FY_YEARS_BACK = 6;

const QUARTERS = ["q1", "q2", "q3", "q4"];

const PERIODS = [
	{ key: "fy", label: "Full Year", quarters: QUARTERS },
	{ key: "h1", label: "H1", quarters: ["q1", "q2"] },
	{ key: "h2", label: "H2", quarters: ["q3", "q4"] },
	{ key: "q1", label: "Q1", quarters: ["q1"] },
	{ key: "q2", label: "Q2", quarters: ["q2"] },
	{ key: "q3", label: "Q3", quarters: ["q3"] },
	{ key: "q4", label: "Q4", quarters: ["q4"] },
];

const SELECT_ALL = "__select_all__";
const NO_ROWS = [];
const RANGE_DATE_FORMAT = "DD MMM YYYY";

const today = () => dayjs().startOf("day");

const currentFyStart = () => {
	const now = dayjs();
	return now.month() >= FY_START_MONTH ? now.year() : now.year() - 1;
};

const FY_OPTIONS = Array.from({ length: FY_YEARS_BACK + 1 }, (_, index) => currentFyStart() - index);

const fyLabel = (startYear) => `FY ${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;

const quarterRange = (fyStart, quarter) => {
	const from = dayjs(new Date(fyStart, FY_START_MONTH, 1)).add(QUARTERS.indexOf(quarter) * 3, "month");
	return { from, to: from.add(3, "month").subtract(1, "day") };
};

const periodMonths = (period) => {
	const first = quarterRange(2000, period.quarters[0]).from;
	const last = quarterRange(2000, period.quarters[period.quarters.length - 1]).to;
	return `${first.format("MMM")} – ${last.format("MMM")}`;
};

const hasStarted = (fy, quarter) => !quarterRange(fy, quarter).from.isAfter(today(), "day");

const isPeriodActive = (period, quarters) =>
	quarters.length > 0 && period.quarters.every((quarter) => quarters.includes(quarter));

const presetRange = (fy, quarters) => {
	const { from } = quarterRange(fy, quarters[0]);
	const { to } = quarterRange(fy, quarters[quarters.length - 1]);
	return { from, to: to.isAfter(today(), "day") ? today() : to };
};

const toggleQuarters = (current, period) => {
	const span = (quarters) => {
		const indexes = quarters.map((quarter) => QUARTERS.indexOf(quarter));
		return QUARTERS.slice(Math.min(...indexes), Math.max(...indexes) + 1);
	};
	if (!isPeriodActive(period, current)) {
		return current.length ? span([...current, ...period.quarters]) : period.quarters;
	}
	const remainder = current.filter((quarter) => !period.quarters.includes(quarter));
	if (!remainder.length) return [];
	return span(remainder).length === remainder.length ? remainder : period.quarters;
};

const presetLabel = (preset) => {
	if (!preset) return "Custom range";
	const { fy, quarters } = preset;
	const period = PERIODS.find(
		(item) => item.quarters.length === quarters.length && isPeriodActive(item, quarters)
	);
	const name = period
		? period.label
		: `${quarters[0].toUpperCase()}–${quarters[quarters.length - 1].toUpperCase()}`;
	return `${fyLabel(fy)} · ${name}`;
};

const formatRange = ({ from, to }) => `${from.format(RANGE_DATE_FORMAT)} – ${to.format(RANGE_DATE_FORMAT)}`;

export const selectionRange = ({ dateFrom: from, dateTo: to }) =>
	from?.isValid?.() && to?.isValid?.() && !from.isAfter(to, "day") ? { from, to } : null;

const defaultFilter = () => {
	const preset = { fy: currentFyStart(), quarters: [...QUARTERS] };
	const { from, to } = presetRange(preset.fy, preset.quarters);
	return { dateFrom: from, dateTo: to, preset, brands: [] };
};

const rorSummaryView = {
	dispatch: defaultFilter(),
	rtv: defaultFilter(),
	rows: [],
	hasRun: false,
	loadedFilters: null,
	ranKey: null,
	brandsInitialized: false,
	grouping: "customer",
	customers: [],
};

const FILTER_GROUPS = [
	{ key: "dispatch", label: "Dispatch", icon: <LocalShippingOutlinedIcon fontSize="small" />, accent: "#1d4ed8" },
	{ key: "rtv", label: "RTV", icon: <AssignmentReturnOutlinedIcon fontSize="small" />, accent: "#b45309" },
];

const isEmpty = (value) => value === null || value === undefined || value === "";

const formatQty = (value) => (isEmpty(value) ? "—" : Number(value).toLocaleString("en-IN"));

const formatAmount = (value) =>
	isEmpty(value)
		? "—"
		: Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatPercent = (value) => (isEmpty(value) ? "—" : `${Number(value).toFixed(2)}%`);

const formatKpiAmount = (value) =>
	isEmpty(value) ? "—" : `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const MEASURES = [
	{ key: "qty", label: "Qty", format: formatQty, kpiFormat: formatQty },
	{ key: "cogs", label: "COGS", format: formatAmount, kpiFormat: formatKpiAmount },
	{ key: "taxable", label: "Taxable Amt", format: formatAmount, kpiFormat: formatKpiAmount },
	{ key: "gst", label: "GST Amt", format: formatAmount, kpiFormat: formatKpiAmount },
	{ key: "total", label: "Total Amt", format: formatAmount, kpiFormat: formatKpiAmount },
];

const ROW_TYPES = [
	{
		id: "dispatch",
		label: "Dispatch",
		color: "#1d4ed8",
		field: (measure) => `dispatch_${measure.key}`,
		brandField: "dispatch_brands",
	},
	{ id: "rtv", label: "RTV", color: "#b45309", field: (measure) => `rtv_${measure.key}`, brandField: "rtv_brands" },
	{ id: "net", label: "NET", color: "#15803d", field: (measure) => `net_${measure.key}`, bg: "#f0fdf4" },
	{
		id: "ror",
		label: "ROR %",
		color: "#111827",
		field: (measure) => `ror_${measure.key}_pct`,
		percent: true,
		bg: "#f8fafc",
	},
];

const formatCell = (type, measure, value) => (type.percent ? formatPercent(value) : measure.format(value));

const rorPercent = (rtv, dispatch) => (Number(dispatch) ? ((Number(rtv) || 0) / Number(dispatch)) * 100 : null);

const computeTotals = (rows) => {
	const sum = (key) => rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
	const result = {};
	MEASURES.forEach((measure) => {
		const dispatch = sum(`dispatch_${measure.key}`);
		const rtv = sum(`rtv_${measure.key}`);
		result[`dispatch_${measure.key}`] = dispatch;
		result[`rtv_${measure.key}`] = rtv;
		result[`net_${measure.key}`] = dispatch - rtv;
		result[`ror_${measure.key}_pct`] = rorPercent(rtv, dispatch);
	});
	return result;
};

const GROUPINGS = [
	{
		key: "customer",
		label: "Customer",
		unit: ["customer", "customers"],
		lead: [{ key: "customer_name", label: "Customer Name", width: 260 }],
		brandList: true,
		groupKey: (row) => row.customer_name,
	},
	{
		key: "customer_brand",
		label: "Customer + Brand",
		unit: ["customer-brand", "customer-brands"],
		lead: [
			{ key: "customer_name", label: "Customer Name", width: 260 },
			{ key: "brand", label: "Brand", width: 140 },
		],
		blockBy: "customer_name",
		groupKey: (row) => `${row.customer_name}|${row.brand}`,
	},
	{
		key: "brand",
		label: "Brand",
		unit: ["brand", "brands"],
		lead: [{ key: "brand", label: "Brand", width: 200 }],
		groupKey: (row) => row.brand,
	},
];

const groupRows = (rawRows, grouping) => {
	const groups = new Map();
	rawRows.forEach((raw) => {
		const key = grouping.groupKey(raw);
		if (!groups.has(key)) {
			const item = { id: key, dispatchBrands: new Set(), rtvBrands: new Set() };
			grouping.lead.forEach((col) => {
				item[col.key] = raw[col.key];
			});
			MEASURES.forEach((measure) => {
				item[`dispatch_${measure.key}`] = null;
				item[`rtv_${measure.key}`] = null;
			});
			groups.set(key, item);
		}
		const item = groups.get(key);
		["dispatch", "rtv"].forEach((side) => {
			MEASURES.forEach((measure) => {
				const value = raw[`${side}_${measure.key}`];
				if (!isEmpty(value)) item[`${side}_${measure.key}`] = (item[`${side}_${measure.key}`] || 0) + Number(value);
			});
		});
		if (!isEmpty(raw.dispatch_qty)) item.dispatchBrands.add(raw.brand);
		if (!isEmpty(raw.rtv_qty)) item.rtvBrands.add(raw.brand);
	});
	return [...groups.values()].map(({ dispatchBrands, rtvBrands, ...item }) => {
		MEASURES.forEach((measure) => {
			const dispatch = item[`dispatch_${measure.key}`];
			const rtv = item[`rtv_${measure.key}`];
			item[`net_${measure.key}`] = dispatch === null && rtv === null ? null : (dispatch || 0) - (rtv || 0);
			item[`ror_${measure.key}_pct`] = rorPercent(rtv, dispatch);
		});
		item.dispatch_brands = [...dispatchBrands].sort().join(", ");
		item.rtv_brands = [...rtvBrands].sort().join(", ");
		return item;
	});
};

const compareByName = (left, right) =>
	compareCell(left.customer_name, right.customer_name, false) || compareCell(left.brand, right.brand, false);

const SORT_OPTIONS = [
	{ key: "name", label: "Name" },
	...ROW_TYPES.flatMap((type) =>
		MEASURES.map((measure) => ({ key: type.field(measure), label: `${type.label} ${measure.label}` }))
	),
];

const groupingToggleSx = {
	flexShrink: 0,
	"& .MuiToggleButton-root": {
		textTransform: "none",
		fontWeight: 600,
		fontSize: "0.75rem",
		px: 1.25,
		height: 36,
		color: "#4b5563",
		borderColor: "#e5e7eb",
		"&.Mui-selected": {
			bgcolor: "#1f2937",
			color: "#fff",
			"&:hover": { bgcolor: "#111827" },
		},
	},
};

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.68rem",
	letterSpacing: 0.25,
	textTransform: "uppercase",
	whiteSpace: "nowrap",
	bgcolor: "#fafafa",
	borderBottom: "1px solid #e5e7eb",
	py: 1,
	px: 1.25,
	color: "#374151",
};

const bodyCellSx = {
	whiteSpace: "nowrap",
	py: 0.75,
	px: 1.25,
	fontSize: "0.8125rem",
	borderBottom: "1px solid #f1f5f9",
	color: "#111827",
};

const compareCell = (a, b, numeric) => {
	if (isEmpty(a) && isEmpty(b)) return 0;
	if (isEmpty(a)) return 1;
	if (isEmpty(b)) return -1;
	if (numeric) return (Number(a) || 0) - (Number(b) || 0);
	return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
};

const presetItemSx = (selected) => ({
	width: "100%",
	justifyContent: "space-between",
	px: 1.25,
	py: 0.75,
	borderRadius: 1,
	fontSize: "0.8125rem",
	fontWeight: 600,
	color: selected ? "#fff" : "#374151",
	bgcolor: selected ? "#111827" : "transparent",
	"&:hover": { bgcolor: selected ? "#111827" : "#f3f4f6" },
	"&.Mui-disabled": { color: "#d1d5db" },
});

function DateRangeButton({ value, onChange }) {
	const [anchor, setAnchor] = useState(null);
	const [draft, setDraft] = useState({ startDate: new Date(), endDate: new Date(), key: "selection" });
	const [draftFy, setDraftFy] = useState(currentFyStart());
	const [draftQuarters, setDraftQuarters] = useState([]);
	const range = selectionRange(value);

	const setDraftRange = (from, to) => setDraft({ startDate: from.toDate(), endDate: to.toDate(), key: "selection" });

	const openRange = (event) => {
		const end = range?.to ?? today();
		setDraftRange(range?.from ?? end, end.isAfter(today(), "day") ? today() : end);
		setDraftFy(value.preset?.fy ?? currentFyStart());
		setDraftQuarters(value.preset?.quarters ?? []);
		setAnchor(event.currentTarget);
	};

	const applyPreset = (fy, quarters) => {
		if (!quarters.length) {
			setDraftFy(fy);
			setDraftQuarters([]);
			setDraftRange(today(), today());
			return;
		}
		const started = quarters.filter((quarter) => hasStarted(fy, quarter));
		const next = started.length ? started : ["q1"];
		const { from, to } = presetRange(fy, next);
		setDraftFy(fy);
		setDraftQuarters(next);
		setDraftRange(from, to);
	};

	const changeFy = (event) => {
		const fy = Number(event.target.value);
		if (draftQuarters.length) applyPreset(fy, draftQuarters);
		else setDraftFy(fy);
	};

	const applyRange = () => {
		const start = dayjs(draft.startDate).startOf("day");
		const end = dayjs(draft.endDate).startOf("day");
		const [dateFrom, dateTo] = start.isAfter(end) ? [end, start] : [start, end];
		onChange({
			dateFrom,
			dateTo,
			preset: draftQuarters.length ? { fy: draftFy, quarters: draftQuarters } : null,
		});
		setAnchor(null);
	};

	return (
		<>
			<Button
				type="button"
				variant="outlined"
				onClick={openRange}
				startIcon={<CalendarTodayRoundedIcon sx={{ fontSize: 18 }} />}
				sx={{
					height: 40,
					minWidth: { xs: "100%", sm: 280 },
					justifyContent: "flex-start",
					textTransform: "none",
					fontWeight: 600,
					borderRadius: 1.5,
					borderColor: "#e5e7eb",
					color: range ? "#111827" : "#6b7280",
					bgcolor: "#fff",
					"&:hover": { borderColor: "#d1d5db", bgcolor: "#fff" },
				}}
			>
				{range ? formatRange(range) : "Select start and end date"}
			</Button>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<GlobalStyles
					styles={{
						".ror-date-range .rdrDayNumber span": { color: "#111827" },
						".ror-date-range .rdrDayPassive .rdrDayNumber span": { color: "#d1d5db" },
						".ror-date-range .rdrStartEdge, .ror-date-range .rdrEndEdge, .ror-date-range .rdrInRange": {
							background: "#111827 !important",
						},
						".ror-date-range .rdrStartEdge ~ .rdrDayNumber span, .ror-date-range .rdrEndEdge ~ .rdrDayNumber span, .ror-date-range .rdrInRange ~ .rdrDayNumber span":
							{ color: "#fff" },
					}}
				/>
				<Stack direction="row">
					<Stack spacing={0.5} sx={{ width: 170, p: 1.25, borderRight: "1px solid #eef0f3" }}>
						<TextField
							select
							size="small"
							label="Financial Year"
							value={draftFy}
							onChange={changeFy}
							sx={{ mb: 1, ...compactInputSx }}
						>
							{FY_OPTIONS.map((year) => (
								<MenuItem key={year} value={year} sx={{ fontSize: "0.8125rem" }}>
									{fyLabel(year)}
								</MenuItem>
							))}
						</TextField>
						{PERIODS.map((period) => {
							const selected = isPeriodActive(period, draftQuarters);
							return (
								<ButtonBase
									key={period.key}
									disabled={!hasStarted(draftFy, period.quarters[0])}
									onClick={() => applyPreset(draftFy, toggleQuarters(draftQuarters, period))}
									sx={presetItemSx(selected)}
								>
									<span>{period.label}</span>
									<Typography
										component="span"
										sx={{ fontSize: "0.7rem", fontWeight: 500, opacity: selected ? 0.8 : 0.6 }}
									>
										{periodMonths(period)}
									</Typography>
								</ButtonBase>
							);
						})}
					</Stack>
					<Box className="ror-date-range">
						<DateRange
							ranges={[draft]}
							onChange={(item) => {
								setDraft(item.selection);
								setDraftQuarters([]);
							}}
							moveRangeOnFirstSelection={false}
							months={2}
							direction="horizontal"
							showDateDisplay={false}
							locale={enUS}
							maxDate={new Date()}
							rangeColors={["#111827"]}
						/>
					</Box>
				</Stack>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={1}
					sx={{ px: 1.5, py: 1, borderTop: "1px solid #eef0f3" }}
				>
					<Typography variant="caption" fontWeight={600} color="#4b5563">
						{presetLabel(draftQuarters.length ? { fy: draftFy, quarters: draftQuarters } : null)}
					</Typography>
					<Stack direction="row" spacing={1}>
						<Button type="button" variant="outlined" onClick={() => setAnchor(null)} sx={outlinedButtonSx}>
							Cancel
						</Button>
						<Button type="button" variant="contained" onClick={applyRange} sx={primaryButtonSx}>
							Apply
						</Button>
					</Stack>
				</Stack>
			</Popover>
		</>
	);
}

function MultiSelect({ options, value, onChange, loading, label, allLabel }) {
	const [query, setQuery] = useState("");

	const visible = useMemo(() => {
		const text = query.trim().toLowerCase();
		if (!text) return options;
		return options.filter((name) => name.toLowerCase().includes(text));
	}, [options, query]);

	const allVisibleSelected = visible.length > 0 && visible.every((name) => value.includes(name));
	const someVisibleSelected = visible.some((name) => value.includes(name)) && !allVisibleSelected;

	const toggleSelectAll = () => {
		if (!visible.length) return;
		if (allVisibleSelected) {
			const hidden = new Set(visible);
			onChange(value.filter((name) => !hidden.has(name)));
			return;
		}
		const next = new Set([...value, ...visible]);
		onChange(options.filter((name) => next.has(name)));
	};

	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			size="small"
			options={[SELECT_ALL, ...options]}
			value={value}
			isOptionEqualToValue={(option, selected) => option === selected}
			getOptionLabel={(option) => (option === SELECT_ALL ? "Select all" : option)}
			filterOptions={(_options, state) => {
				const text = state.inputValue.trim().toLowerCase();
				const matched = options.filter((name) => !text || name.toLowerCase().includes(text));
				return matched.length ? [SELECT_ALL, ...matched] : [];
			}}
			onChange={(_, next, reason) => {
				if (next.includes(SELECT_ALL)) {
					toggleSelectAll();
					return;
				}
				onChange(reason === "clear" ? [] : next);
			}}
			onInputChange={(_, text) => setQuery(text)}
			loading={loading}
			sx={{ "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap", overflow: "hidden" } }}
			renderOption={(props, option) => {
				const { key: _optionKey, ...optionProps } = props;
				const isAll = option === SELECT_ALL;
				return (
					<li key={option} {...optionProps}>
						<Checkbox
							size="small"
							checked={isAll ? allVisibleSelected : value.includes(option)}
							indeterminate={isAll && someVisibleSelected}
							sx={{ p: 0.5, mr: 1 }}
						/>
						<ListItemText
							primary={
								isAll
									? query.trim()
										? `Select all matching (${visible.length})`
										: "Select all"
									: option
							}
							primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: isAll ? 600 : 400 }}
						/>
					</li>
				);
			}}
			renderTags={(selected, getTagProps) => {
				if (selected.length === options.length && options.length > 0) {
					return <Chip label={allLabel} size="small" onDelete={() => onChange([])} />;
				}
				return selected.slice(0, 1).map((option, index) => (
					<Chip
						{...getTagProps({ index })}
						key={option}
						label={selected.length > 1 ? `${option} +${selected.length - 1}` : option}
						size="small"
						sx={{ maxWidth: 220 }}
					/>
				));
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					placeholder={value.length ? "" : allLabel}
					sx={compactInputSx}
				/>
			)}
		/>
	);
}

function FilterGroup({ group, value, onChange, brands, brandsLoading }) {
	return (
		<Panel sx={{ p: 2, height: "100%", borderTop: `3px solid ${group.accent}` }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1.75}>
				<Stack direction="row" spacing={1} alignItems="center" sx={{ color: group.accent }}>
					{group.icon}
					<Typography variant="subtitle2" fontWeight={700} color="#111827">
						{group.label}
					</Typography>
				</Stack>
				<Chip
					label={presetLabel(value.preset)}
					size="small"
					sx={{ height: 24, fontWeight: 600, fontSize: "0.7rem", bgcolor: "#f3f4f6", color: "#374151" }}
				/>
			</Stack>

			<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
				<DateRangeButton value={value} onChange={(next) => onChange({ ...value, ...next })} />
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<MultiSelect
						label="Brand"
						allLabel="All brands"
						options={brands}
						value={value.brands}
						onChange={(next) => onChange({ ...value, brands: next })}
						loading={brandsLoading}
					/>
				</Box>
			</Stack>
		</Panel>
	);
}

function RorTable({
	rows,
	grouping,
	onGroupingChange,
	customerOptions,
	customers,
	onCustomersChange,
	loading,
	hasRun,
	stale,
}) {
	const [sort, setSort] = useState({ key: "name", dir: "asc" });
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const columnCount = grouping.lead.length + 1 + (grouping.brandList ? 1 : 0) + MEASURES.length;

	useEffect(() => setPage(0), [rows, sort]);

	const blocks = useMemo(() => {
		const dir = sort.dir === "desc" ? -1 : 1;
		const compare = (left, right) =>
			(sort.key === "name" ? compareByName(left, right) : compareCell(left[sort.key], right[sort.key], true)) * dir;
		if (!grouping.blockBy) {
			return [...rows].sort(compare).map((row) => ({ id: row.id, rows: [row] }));
		}
		const byBlock = new Map();
		rows.forEach((row) => {
			const key = row[grouping.blockBy];
			if (!byBlock.has(key)) byBlock.set(key, []);
			byBlock.get(key).push(row);
		});
		return [...byBlock.entries()]
			.map(([key, blockRows]) => ({
				id: key,
				rows: [...blockRows].sort(compare),
				summary: { ...computeTotals(blockRows), [grouping.blockBy]: key },
			}))
			.sort((left, right) => compare(left.summary, right.summary));
	}, [rows, sort, grouping]);

	const pagedBlocks = blocks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const blockBorder = grouping.blockBy ? "2px solid #9ca3af" : "1px solid #d1d5db";

	const renderLeadCells = (block, row, rowIndex) =>
		grouping.lead.map((col, colIndex) => {
			const spansBlock = grouping.blockBy === col.key;
			if (spansBlock && rowIndex !== 0) return null;
			const isLastRow = rowIndex === block.rows.length - 1;
			return (
				<TableCell
					key={col.key}
					rowSpan={spansBlock ? block.rows.length * ROW_TYPES.length : ROW_TYPES.length}
					sx={{
						...bodyCellSx,
						bgcolor: "#fff",
						fontWeight: 600,
						maxWidth: colIndex === 0 ? 320 : 200,
						overflow: "hidden",
						textOverflow: "ellipsis",
						verticalAlign: "middle",
						borderRight: "1px solid #eef0f3",
						borderBottom: spansBlock || isLastRow ? blockBorder : "1px solid #d1d5db",
						...(colIndex === 0 ? { position: "sticky", left: 0, zIndex: 2 } : {}),
					}}
				>
					<Tooltip title={row[col.key] || ""} placement="top" enterDelay={250}>
						<span>{row[col.key]}</span>
					</Tooltip>
				</TableCell>
			);
		});

	const renderBlock = (block) =>
		block.rows.flatMap((row, rowIndex) =>
			ROW_TYPES.map((type, typeIndex) => {
				const isLastType = typeIndex === ROW_TYPES.length - 1;
				const endsBlock = isLastType && rowIndex === block.rows.length - 1;
				const rowBorder = endsBlock ? blockBorder : isLastType ? "1px solid #d1d5db" : "1px solid #f1f5f9";
				return (
					<TableRow key={`${row.id}-${type.id}`}>
						{typeIndex === 0 ? renderLeadCells(block, row, rowIndex) : null}
						<TableCell
							sx={{
								...bodyCellSx,
								borderBottom: rowBorder,
								bgcolor: type.bg,
								fontWeight: 700,
								fontSize: "0.75rem",
								color: type.color,
							}}
						>
							{type.label}
						</TableCell>
						{grouping.brandList ? (
							<TableCell
								sx={{
									...bodyCellSx,
									borderBottom: rowBorder,
									bgcolor: type.bg,
									maxWidth: 260,
									overflow: "hidden",
									textOverflow: "ellipsis",
									fontSize: "0.75rem",
									color: "#4b5563",
								}}
							>
								{type.brandField && row[type.brandField] ? (
									<Tooltip title={row[type.brandField]} placement="top" enterDelay={250}>
										<span>{row[type.brandField]}</span>
									</Tooltip>
								) : (
									"—"
								)}
							</TableCell>
						) : null}
						{MEASURES.map((measure) => (
							<TableCell
								key={measure.key}
								align="right"
								sx={{
									...bodyCellSx,
									borderBottom: rowBorder,
									bgcolor: type.bg,
									fontWeight: type.bg ? 700 : 500,
									color: Number(row[type.field(measure)]) < 0 ? "#b91c1c" : "#111827",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{formatCell(type, measure, row[type.field(measure)])}
							</TableCell>
						))}
					</TableRow>
				);
			})
		);

	return (
		<>
			<Stack
				direction={{ xs: "column", md: "row" }}
				spacing={1}
				alignItems={{ md: "center" }}
				sx={{ px: 2, py: 1, borderBottom: "1px solid #eef0f3" }}
			>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={grouping.key}
					onChange={(_, next) => next && onGroupingChange(next)}
					sx={groupingToggleSx}
				>
					{GROUPINGS.map((option) => (
						<ToggleButton key={option.key} value={option.key}>
							{option.label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
				<Box sx={{ width: { xs: "100%", sm: 320 } }}>
					<MultiSelect
						label="Customer"
						allLabel="All customers"
						options={customerOptions}
						value={customers}
						onChange={onCustomersChange}
					/>
				</Box>
				<Stack direction="row" spacing={0.5} alignItems="center">
					<TextField
						select
						size="small"
						label="Sort by"
						value={sort.key}
						onChange={(event) =>
							setSort({ key: event.target.value, dir: event.target.value === "name" ? "asc" : "desc" })
						}
						sx={{ width: { xs: "100%", sm: 220 }, ...compactInputSx }}
					>
						{SORT_OPTIONS.map((option) => (
							<MenuItem key={option.key} value={option.key} sx={{ fontSize: "0.8125rem" }}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
					<Tooltip title={sort.dir === "asc" ? "Ascending" : "Descending"}>
						<IconButton
							size="small"
							onClick={() => setSort((current) => ({ ...current, dir: current.dir === "asc" ? "desc" : "asc" }))}
							sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5 }}
						>
							{sort.dir === "asc" ? (
								<ArrowUpwardRoundedIcon fontSize="small" />
							) : (
								<ArrowDownwardRoundedIcon fontSize="small" />
							)}
						</IconButton>
					</Tooltip>
				</Stack>
			</Stack>
			<TableContainer sx={{ maxHeight: "calc(100vh - 420px)", minHeight: 240, overflow: "auto" }}>
				<Table stickyHeader size="small" sx={{ minWidth: 1280 }}>
					<TableHead>
						<TableRow>
							{grouping.lead.map((col, index) => (
								<TableCell
									key={col.key}
									sx={{
										...headerCellSx,
										minWidth: col.width,
										...(index === 0
											? { position: "sticky", left: 0, zIndex: 4, borderRight: "1px solid #e5e7eb" }
											: {}),
									}}
								>
									{col.label}
								</TableCell>
							))}
							<TableCell sx={{ ...headerCellSx, minWidth: 100 }}>Type</TableCell>
							{grouping.brandList ? (
								<TableCell sx={{ ...headerCellSx, minWidth: 180 }}>Brand</TableCell>
							) : null}
							{MEASURES.map((measure) => (
								<TableCell key={measure.key} align="right" sx={{ ...headerCellSx, minWidth: 140 }}>
									{measure.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={columnCount} align="center" sx={{ py: 8, borderBottom: 0 }}>
									<Stack alignItems="center" spacing={1.25}>
										<CircularProgress size={28} />
										<Typography color="text.secondary" variant="body2">
											Loading...
										</Typography>
									</Stack>
								</TableCell>
							</TableRow>
						) : (
							pagedBlocks.flatMap(renderBlock)
						)}
						{!loading && rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={columnCount} align="center" sx={{ py: 6, borderBottom: 0 }}>
									<Typography color="text.secondary">
										{!hasRun
											? "Choose the dispatch and RTV filters, then click Run"
											: stale
												? "Filters have changed. Click Run to load data for the new filters"
												: "No dispatch or RTV found for these filters"}
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={blocks.length}
				page={page}
				onPageChange={(_, nextPage) => setPage(nextPage)}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={(event) => {
					setRowsPerPage(parseInt(event.target.value, 10));
					setPage(0);
				}}
				rowsPerPageOptions={[25, 50, 100]}
				labelRowsPerPage={grouping.blockBy ? "Customers per page" : `${grouping.label} rows per page`}
			/>
		</>
	);
}

const EXPORT_TYPES = [
	{ key: "summary", label: "Summary" },
	{ key: "dump", label: "Dump data" },
];

const EXPORT_LEVELS = [
	{ key: "invoice", label: "Invoice level", hint: "One row per invoice with brands, qty, COGS, taxable, GST and total." },
	{
		key: "barcode",
		label: "Barcode level",
		hint: "One row per barcode line with brand, style, colour, size, season, cost rate, COGS and amounts.",
	},
];

const sectionLabelSx = { fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", letterSpacing: 0.4, mb: 0.75 };

const saveBlob = (blob, filename) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const exportErrorMessage = async (err) => {
	const data = err?.response?.data;
	if (data instanceof Blob) {
		const text = await data.text().catch(() => "");
		try {
			return JSON.parse(text)?.message || text || err?.message || "Failed to export";
		} catch {
			return text || err?.message || "Failed to export";
		}
	}
	return (typeof data === "string" && data.trim()) || data?.message || err?.message || "Failed to export";
};

function ExportDialog({
	onClose,
	filters,
	brands,
	brandsLoading,
	customerOptions,
	customers,
	summaryReady,
	groupingLabel,
	onExportSummary,
	showSnack,
}) {
	const [type, setType] = useState(summaryReady ? "summary" : "dump");
	const [level, setLevel] = useState("invoice");
	const [sources, setSources] = useState(FILTER_GROUPS.map((group) => group.key));
	const [draft, setDraft] = useState(filters);
	const [draftCustomers, setDraftCustomers] = useState(customers);
	const [progress, setProgress] = useState("");
	const busy = Boolean(progress);
	const levelInfo = EXPORT_LEVELS.find((option) => option.key === level);

	const toggleSource = (key) =>
		setSources((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

	const downloadDump = async () => {
		const groups = FILTER_GROUPS.filter((group) => sources.includes(group.key));
		const missing = groups.find((group) => !selectionRange(draft[group.key]));
		if (missing) {
			showSnack(`Select a date range for ${missing.label}`, "error");
			return;
		}
		setProgress(`Preparing ${groups.map((group) => group.label).join(" + ")} ${level} file...`);
		try {
			const res = await pythonVmInstance.post(
				"/mbo-lfs-qty-reco/ror-summary/export",
				{
					level,
					customers: draftCustomers,
					sources: groups.map((group) => {
						const range = selectionRange(draft[group.key]);
						return {
							source: group.key,
							date_from: range.from.format("YYYY-MM-DD"),
							date_to: range.to.format("YYYY-MM-DD"),
							brands: draft[group.key].brands,
						};
					}),
				},
				{ responseType: "blob" }
			);
			if (res.headers?.["x-row-count"] === "0") {
				showSnack("No rows for the selected filters", "info");
				return;
			}
			const dispatchRange = selectionRange(draft.dispatch);
			const suffix =
				sources.includes("dispatch") && dispatchRange
					? `_${dispatchRange.from.format("YYYYMMDD")}_${dispatchRange.to.format("YYYYMMDD")}`
					: "";
			saveBlob(res.data, `mbo_lfs_ror_${groups.map((group) => group.key).join("_")}_${level}${suffix}.xlsx`);
			onClose();
		} catch (err) {
			showSnack(await exportErrorMessage(err), "error");
		} finally {
			setProgress("");
		}
	};

	const download = () => {
		if (type === "summary") {
			onExportSummary();
			onClose();
			return;
		}
		downloadDump();
	};

	const canDownload = type === "summary" ? summaryReady : sources.length > 0;

	return (
		<Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 1 }}>Export</DialogTitle>
			<DialogContent dividers>
				<Typography sx={sectionLabelSx}>WHAT TO EXPORT</Typography>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={type}
					onChange={(_, next) => next && setType(next)}
					disabled={busy}
					sx={{ ...groupingToggleSx, mb: 1 }}
				>
					{EXPORT_TYPES.map((option) => (
						<ToggleButton key={option.key} value={option.key}>
							{option.label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>

				{type === "summary" ? (
					<Typography variant="body2" color={summaryReady ? "text.secondary" : "#b45309"}>
						{summaryReady
							? `Downloads the ${groupingLabel.toLowerCase()}-wise table as shown, with Dispatch, RTV, NET and ROR % rows and the current customer selection.`
							: "Run the summary with the current filters first, or choose Dump data."}
					</Typography>
				) : (
					<>
						<Typography sx={{ ...sectionLabelSx, mt: 1.5 }}>LEVEL</Typography>
						<ToggleButtonGroup
							exclusive
							size="small"
							value={level}
							onChange={(_, next) => next && setLevel(next)}
							disabled={busy}
							sx={groupingToggleSx}
						>
							{EXPORT_LEVELS.map((option) => (
								<ToggleButton key={option.key} value={option.key}>
									{option.label}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
						<Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
							{levelInfo.hint}
						</Typography>

						<Typography sx={{ ...sectionLabelSx, mt: 2 }}>DATA</Typography>
						<Stack direction="row" spacing={2}>
							{FILTER_GROUPS.map((group) => (
								<FormControlLabel
									key={group.key}
									disabled={busy}
									control={
										<Checkbox
											size="small"
											checked={sources.includes(group.key)}
											onChange={() => toggleSource(group.key)}
										/>
									}
									label={group.label}
									sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
								/>
							))}
						</Stack>
						<Stack spacing={1.5} mt={1}>
							{FILTER_GROUPS.filter((group) => sources.includes(group.key)).map((group) => (
								<FilterGroup
									key={group.key}
									group={group}
									value={draft[group.key]}
									onChange={(next) => setDraft((current) => ({ ...current, [group.key]: next }))}
									brands={brands}
									brandsLoading={brandsLoading}
								/>
							))}
						</Stack>

						<Typography sx={{ ...sectionLabelSx, mt: 2 }}>CUSTOMERS</Typography>
						<MultiSelect
							label="Customer"
							allLabel="All customers"
							options={customerOptions}
							value={draftCustomers}
							onChange={setDraftCustomers}
						/>
						<Typography variant="caption" color="text.secondary" display="block" mt={1}>
							Downloads one Excel file with a separate sheet for each selected data type. A full year at
							barcode level can take 2–3 minutes.
						</Typography>
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 1.5 }}>
				{progress ? (
					<Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
						{progress}
					</Typography>
				) : null}
				<Button onClick={onClose} disabled={busy} sx={outlinedButtonSx}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={busy || !canDownload}
					onClick={download}
					startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
					sx={primaryButtonSx}
				>
					{busy ? "Downloading..." : "Download"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

const describeFilter = (filter) => {
	const range = selectionRange(filter);
	const brandText = filter.brands.length
		? `${filter.brands.length} brand${filter.brands.length === 1 ? "" : "s"}`
		: "All brands";
	return `${range ? formatRange(range) : "No dates"} · ${brandText}`;
};

const filtersKey = (filters) =>
	JSON.stringify(
		FILTER_GROUPS.map(({ key }) => {
			const range = selectionRange(filters[key]);
			return [
				range?.from.format("YYYY-MM-DD") ?? null,
				range?.to.format("YYYY-MM-DD") ?? null,
				[...filters[key].brands].sort(),
			];
		})
	);

function RorSummary() {
	const { loginUser, setSnackBar, setSnackBarOpen } = GlobalState();
	const viewer = isMboLfsQtyViewer(loginUser);
	const [filters, setFilters] = useState(() => ({
		dispatch: rorSummaryView.dispatch,
		rtv: rorSummaryView.rtv,
	}));
	const [brands, setBrands] = useState([]);
	const [brandsLoading, setBrandsLoading] = useState(false);
	const [rows, setRows] = useState(rorSummaryView.rows);
	const [hasRun, setHasRun] = useState(rorSummaryView.hasRun);
	const [loadedFilters, setLoadedFilters] = useState(rorSummaryView.loadedFilters);
	const [ranKey, setRanKey] = useState(rorSummaryView.ranKey);
	const [groupingKey, setGroupingKey] = useState(rorSummaryView.grouping);
	const [customers, setCustomers] = useState(rorSummaryView.customers);
	const [loading, setLoading] = useState(false);
	const [exportOpen, setExportOpen] = useState(false);
	const loadToken = useRef(0);

	const grouping = GROUPINGS.find((option) => option.key === groupingKey) || GROUPINGS[0];
	const customerOptions = useMemo(
		() => [...new Set(rows.map((row) => row.customer_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
		[rows]
	);
	const visibleRows = useMemo(() => {
		if (!customers.length) return rows;
		const selected = new Set(customers);
		return rows.filter((row) => selected.has(row.customer_name));
	}, [rows, customers]);
	const groupedRows = useMemo(() => groupRows(visibleRows, grouping), [visibleRows, grouping]);
	const totals = useMemo(() => computeTotals(visibleRows), [visibleRows]);
	const currentKey = useMemo(() => filtersKey(filters), [filters]);
	const stale = hasRun && ranKey !== currentKey;
	const showData = hasRun && !stale;

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const runSummary = async () => {
		const dispatchRange = selectionRange(filters.dispatch);
		const rtvRange = selectionRange(filters.rtv);
		if (!dispatchRange || !rtvRange) {
			showSnack("Select a date range for both Dispatch and RTV", "error");
			return;
		}
		const token = ++loadToken.current;
		const requestKey = currentKey;
		setLoading(true);
		setRows([]);
		setHasRun(false);
		try {
			const res = await pythonVmInstance.post("/mbo-lfs-qty-reco/ror-summary", {
				dispatch_from: dispatchRange.from.format("YYYY-MM-DD"),
				dispatch_to: dispatchRange.to.format("YYYY-MM-DD"),
				dispatch_brands: filters.dispatch.brands,
				rtv_from: rtvRange.from.format("YYYY-MM-DD"),
				rtv_to: rtvRange.to.format("YYYY-MM-DD"),
				rtv_brands: filters.rtv.brands,
			});
			if (loadToken.current !== token) return;
			const nextRows = res?.data?.result?.rows || [];
			const loadedCustomers = new Set(nextRows.map((row) => row.customer_name));
			setRows(nextRows);
			setCustomers((current) => current.filter((name) => loadedCustomers.has(name)));
			setLoadedFilters({ dispatch: describeFilter(filters.dispatch), rtv: describeFilter(filters.rtv) });
			setRanKey(requestKey);
			setHasRun(true);
		} catch (err) {
			if (loadToken.current !== token) return;
			const data = err?.response?.data;
			showSnack(
				(typeof data === "string" && data.trim()) || data?.message || err?.message || "Failed to load ROR summary",
				"error"
			);
		} finally {
			if (loadToken.current === token) setLoading(false);
		}
	};

	const exportRows = () => {
		const columns = [
			...grouping.lead.map((col) => ({ key: col.key, label: col.label.toUpperCase() })),
			{ key: "type", label: "TYPE" },
			...(grouping.brandList ? [{ key: "brand_list", label: "BRAND" }] : []),
			...MEASURES.map((measure) => ({ key: measure.key, label: measure.label.toUpperCase() })),
		];
		const exportData = [...groupedRows].sort(compareByName).flatMap((row) =>
			ROW_TYPES.map((type) => ({
				...Object.fromEntries(grouping.lead.map((col) => [col.key, row[col.key]])),
				type: type.label,
				brand_list: type.brandField ? row[type.brandField] : "",
				...Object.fromEntries(
					MEASURES.map((measure) => {
						const value = row[type.field(measure)];
						return [measure.key, type.percent && !isEmpty(value) ? Number(Number(value).toFixed(2)) : value];
					})
				),
			}))
		);
		const dispatchRange = selectionRange(filters.dispatch);
		const suffix = dispatchRange ? `${dispatchRange.from.format("YYYYMMDD")}_${dispatchRange.to.format("YYYYMMDD")}` : "";
		downloadTable(`mbo_lfs_ror_summary_${grouping.key}_${suffix}.xlsx`, columns, exportData);
	};

	useEffect(() => {
		let active = true;
		(async () => {
			setBrandsLoading(true);
			try {
				const res = await pythonVmInstance.get("/mbo-lfs-qty-reco/brands");
				if (!active) return;
				const list = res?.data?.result?.brands || [];
				setBrands(list);
				if (!rorSummaryView.brandsInitialized && list.length && loadToken.current === 0) {
					rorSummaryView.brandsInitialized = true;
					const selectAll = (filter) => (filter.brands.length ? filter : { ...filter, brands: [...list] });
					setFilters((current) => ({ dispatch: selectAll(current.dispatch), rtv: selectAll(current.rtv) }));
				}
			} catch {
				if (active) setBrands([]);
			} finally {
				if (active) setBrandsLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		rorSummaryView.dispatch = filters.dispatch;
		rorSummaryView.rtv = filters.rtv;
		rorSummaryView.rows = rows;
		rorSummaryView.hasRun = hasRun;
		rorSummaryView.loadedFilters = loadedFilters;
		rorSummaryView.ranKey = ranKey;
		rorSummaryView.grouping = groupingKey;
		rorSummaryView.customers = customers;
	}, [filters, rows, hasRun, loadedFilters, ranKey, groupingKey, customers]);

	const updateFilter = (key) => (next) => setFilters((current) => ({ ...current, [key]: next }));

	return (
		<Box>
			<Grid container spacing={1.5} mb={1.5}>
				{FILTER_GROUPS.map((group) => (
					<Grid item xs={12} lg key={group.key}>
						<FilterGroup
							group={group}
							value={filters[group.key]}
							onChange={updateFilter(group.key)}
							brands={brands}
							brandsLoading={brandsLoading}
						/>
					</Grid>
				))}
				<Grid item xs={12} lg="auto">
					<Stack
						direction={{ xs: "row", lg: "column" }}
						spacing={1}
						justifyContent={{ xs: "flex-end", lg: "center" }}
						sx={{ height: "100%" }}
					>
						<Button
							variant="contained"
							size="small"
							disabled={loading}
							onClick={runSummary}
							startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshOutlinedIcon />}
							sx={{ ...primaryButtonSx, height: 36, minWidth: 120 }}
						>
							{loading ? "Running..." : "Run"}
						</Button>
						{viewer ? null : (
							<Button
								variant="outlined"
								size="small"
								disabled={loading}
								startIcon={<DownloadOutlinedIcon />}
								onClick={() => setExportOpen(true)}
								sx={{ ...outlinedButtonSx, height: 36, minWidth: 120 }}
							>
								Export
							</Button>
						)}
					</Stack>
				</Grid>
			</Grid>

			<Panel sx={{ mb: 1.5, overflow: "hidden" }}>
				{ROW_TYPES.map((type) => (
					<Stack
						key={type.id}
						direction="row"
						alignItems="stretch"
						sx={{ "&:not(:last-of-type)": { borderBottom: "1px solid #eef0f3" } }}
					>
						<Box
							sx={{
								width: 96,
								flexShrink: 0,
								px: 1.5,
								display: "flex",
								alignItems: "center",
								borderRight: "1px solid #eef0f3",
								borderLeft: `3px solid ${type.color}`,
								bgcolor: "#fafafa",
							}}
						>
							<Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: type.color }}>
								{type.label}
							</Typography>
						</Box>
						{MEASURES.map((measure) => {
							const value = totals[type.field(measure)];
							return (
								<KpiStat
									key={measure.key}
									label={measure.label}
									value={
										!showData ? "—" : type.percent ? formatPercent(value) : measure.kpiFormat(value)
									}
								/>
							);
						})}
					</Stack>
				))}
			</Panel>

			<Panel sx={{ overflow: "hidden" }}>
				<Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #eef0f3" }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="subtitle2" fontWeight={700} color="#111827">
							{grouping.label}-wise Dispatch vs RTV
						</Typography>
						<Chip
							label={`${showData ? groupedRows.length : 0} ${
								showData && groupedRows.length === 1 ? grouping.unit[0] : grouping.unit[1]
							}`}
							size="small"
							sx={{ height: 22, fontWeight: 600, fontSize: "0.7rem", bgcolor: "#f3f4f6", color: "#374151" }}
						/>
						{stale ? (
							<Chip
								label="Filters changed · click Run"
								size="small"
								sx={{ height: 22, fontWeight: 600, fontSize: "0.7rem", bgcolor: "#fef3c7", color: "#92400e" }}
							/>
						) : null}
					</Stack>
					{showData && loadedFilters ? (
						<Typography variant="caption" color="text.secondary">
							Dispatch: {loadedFilters.dispatch} · RTV: {loadedFilters.rtv}
						</Typography>
					) : null}
				</Box>
				<RorTable
					rows={showData ? groupedRows : NO_ROWS}
					grouping={grouping}
					onGroupingChange={setGroupingKey}
					customerOptions={customerOptions}
					customers={customers}
					onCustomersChange={setCustomers}
					loading={loading}
					hasRun={hasRun}
					stale={stale}
				/>
			</Panel>
			{exportOpen ? (
				<ExportDialog
					onClose={() => setExportOpen(false)}
					filters={filters}
					brands={brands}
					brandsLoading={brandsLoading}
					customerOptions={customerOptions}
					customers={customers}
					summaryReady={showData && rows.length > 0}
					groupingLabel={grouping.label}
					onExportSummary={exportRows}
					showSnack={showSnack}
				/>
			) : null}
		</Box>
	);
}

export default RorSummary;
